import { PageEmpty, PageHeader } from "@majitap/ui";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import type React from "react";
import { ASSISTANT_ACCESS, landingFor, nothingInConsole } from "./guards";
import { allowed, type ConsoleItem, MAIN_MENU, SETTINGS_MENU } from "./nav";
import { AccountPage } from "./routes/account";
import { AssistantAccessPage } from "./routes/assistant-access";
import { BusinessesPage, businessFilters } from "./routes/businesses/list";
import { BUSINESS_TABS, BusinessPage } from "./routes/businesses/record";
import { ChooseCompanyPage } from "./routes/choose-company";
import { ConsoleLayout } from "./routes/console-layout";
import { CustomersPage, customerFilters } from "./routes/customers/list";
import { CUSTOMER_TABS, CustomerPage } from "./routes/customers/record";
import { DeliveriesPage, deliveryFilters } from "./routes/deliveries/list";
import { DeliveryPage } from "./routes/deliveries/page";
import { KitPage, kitFilters } from "./routes/dev/kit";
import { boardFilters, DispatchPage } from "./routes/dispatch/board";
import { FleetMapPage, fleetMapFilters } from "./routes/fleet/map";
import { FleetPage, fleetFilters } from "./routes/fleet/page";
import { ForgotPasswordPage } from "./routes/forgot-password";
import { IncidentsPage, incidentFilters } from "./routes/incidents/list";
import { EditInvoicePage, NewInvoicePage } from "./routes/invoices/form";
import { InvoicesPage, invoiceFilters } from "./routes/invoices/list";
import { InvoicePage } from "./routes/invoices/record";
import { OrdersPage, orderFilters } from "./routes/orders/list";
import { NewOrderPage } from "./routes/orders/new";
import { OrderPage } from "./routes/orders/record";
import { OverviewPage } from "./routes/overview";
import { ComingSoon, Gated } from "./routes/pages";
import { PartnersPage, partnerFilters } from "./routes/partners/list";
import { PARTNER_TABS, PartnerPage } from "./routes/partners/record";
import { PaymentsPage, paymentFilters } from "./routes/payments/list";
import { PaymentPage } from "./routes/payments/record";
import { PayoutsPage, payoutFilters } from "./routes/payouts/list";
import { PayoutPage } from "./routes/payouts/record";
import { PlanPage } from "./routes/plan/page";
import { PricingPage, pricingFilters } from "./routes/pricing/page";
import { RulePage } from "./routes/pricing/rule";
import { ProductsPage } from "./routes/products/list";
import { ProductPage } from "./routes/products/record";
import { SensorsPage, sensorFilters } from "./routes/sensors/page";
import { SetPasswordPage } from "./routes/set-password";
import { AccountingPage, accountingFilters } from "./routes/settings/accounting";
import { AlertsPage } from "./routes/settings/alerts";
import { ApiTokensPage, tokenFilters } from "./routes/settings/api-tokens";
import { AuditPage, auditFilters } from "./routes/settings/audit";
import { BrandingPage } from "./routes/settings/branding";
import { DeliveryEtaPage } from "./routes/settings/delivery-eta";
import { DispatchAlertsPage } from "./routes/settings/dispatch-alerts";
import { FailedEventsPage } from "./routes/settings/failed-events";
import { IntegrationPage } from "./routes/settings/integration";
import { IntegrationsPage, integrationFilters } from "./routes/settings/integrations";
import { InvoiceDetailsPage } from "./routes/settings/invoice-details";
import { StaffMemberPage } from "./routes/settings/member";
import { ProofChecklistPage } from "./routes/settings/proof-checklist";
import { ReadinessPage, readinessFilters } from "./routes/settings/readiness";
import { ShiftChecklistPage } from "./routes/settings/shift-checklist";
import { StaffPage, staffFilters } from "./routes/settings/staff";
import { StatusReasonsPage } from "./routes/settings/status-reasons";
import { WebhooksPage, webhookFilters } from "./routes/settings/webhooks";
import { SignInPage } from "./routes/sign-in";
import { VouchersPage, voucherFilters } from "./routes/vouchers/page";
import { WalletsPage, walletFilters } from "./routes/wallets/list";
import { WalletPage } from "./routes/wallets/record";
import { ZonesPage, zoneFilters } from "./routes/zones/page";
import { meQuery } from "./session";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

/** /v1/me, fresh enough for a guard: the cached copy unless it's older than the query's staleTime. */
const me = (context: RouterContext) => context.queryClient.ensureQueryData(meQuery);

export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: (search: Record<string, unknown>): { next?: string; notice?: "password-set" } => ({
    next: typeof search.next === "string" ? search.next : undefined,
    notice: search.notice === "password-set" ? "password-set" : undefined,
  }),
  // already signed in: go on
  beforeLoad: async ({ context, search }) => {
    const session = await me(context);
    if (session) throw redirect({ href: landingFor(session, search.next) });
  },
  component: SignInPage,
});

export const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/set-password",
  // reset=1: from Forgot password, not an invitation
  validateSearch: (search: Record<string, unknown>): { token?: string; email?: string; reset?: true } => ({
    token: typeof search.token === "string" ? search.token : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
    reset: search.reset === "1" || undefined,
  }),
  component: SetPasswordPage,
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  component: ForgotPasswordPage,
});

export const chooseCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/choose-company",
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  beforeLoad: async ({ context }) => {
    if (!(await me(context))) throw redirect({ to: "/sign-in" });
  },
  component: ChooseCompanyPage,
});

/** Staff whose roles open nothing in the console: their access is in the assistant (P15-23). */
export const assistantAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ASSISTANT_ACCESS,
  beforeLoad: async ({ context }) => {
    const session = await me(context);
    if (!session) throw redirect({ to: "/sign-in" });
    if (!session.active_company) throw redirect({ to: "/choose-company" });
    if (!nothingInConsole(session)) throw redirect({ to: "/" });
  },
  component: AssistantAccessPage,
});

/** Every console page: signed in, with a company chosen, inside the App Shell. */
const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "console",
  beforeLoad: async ({ context, location }) => {
    const session = await me(context);
    if (!session) throw redirect({ to: "/sign-in", search: { next: location.href } });
    if (!session.active_company) throw redirect({ to: "/choose-company", search: { next: location.href } });
    if (nothingInConsole(session)) throw redirect({ href: ASSISTANT_ACCESS });
  },
  component: ConsoleLayout,
  notFoundComponent: () => (
    <>
      <PageHeader title="Page not found" />
      <PageEmpty title="There's no page here" description="Check the address, or use the menu to find the page." />
    </>
  ),
});

/** One route per menu item, gated by its permission. Pages replace ComingSoon as they are built. */
const pageFor = (item: ConsoleItem) =>
  createRoute({
    getParentRoute: () => consoleRoute,
    path: item.path,
    component: () => (
      <Gated item={item}>{item.id === "overview" ? <OverviewPage item={item} /> : <ComingSoon item={item} />}</Gated>
    ),
  });

const menuItems = [...MAIN_MENU, ...SETTINGS_MENU].flatMap((section) => section.items);
const itemById = (id: string) => menuItems.find((i) => i.id === id)!;

/** Pages that are built, with their own typed routes; every other menu item shows ComingSoon. */
const BUILT = new Set([
  "orders",
  "deliveries",
  "dispatch",
  "plan",
  "incidents",
  "customers",
  "businesses",
  "fleet",
  "live-map",
  "partners",
  "invoices",
  "payments",
  "wallets",
  "payouts",
  "products",
  "pricing",
  "vouchers",
  "zones",
  "staff",
  "audit",
  "integrations",
  "accounting",
  "sensors",
  "api-tokens",
  "webhooks",
  "failed-events",
  "readiness",
  "shift-checklist",
  "proof-checklist",
  "dispatch-alerts",
  "delivery-eta",
  "alerts",
  "status-reasons",
  "branding",
  "invoice-details",
]);
const menuRoutes = menuItems.filter((item) => !BUILT.has(item.id)).map(pageFor);

const gated = (id: string, Page: () => React.ReactNode) => () => (
  <Gated item={itemById(id)}>
    <Page />
  </Gated>
);

// Orders (P8-07)
const ordersRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/orders",
  validateSearch: orderFilters.validate,
  component: gated("orders", OrdersPage),
});
const newOrderRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/orders/new",
  // ?business_id= starts an order for that business (its page's New order)
  validateSearch: (s: Record<string, unknown>): { business_id?: string } =>
    typeof s.business_id === "string" ? { business_id: s.business_id } : {},
  component: gated("orders", NewOrderPage),
});
const orderRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/orders/$orderId",
  component: gated("orders", OrderPage),
});
// One delivery, under its order (P8-48)
const deliveryRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/orders/$orderId/deliveries/$deliveryId",
  component: gated("orders", DeliveryPage),
});
// Deliveries, on any day (P8-57)
const deliveriesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/deliveries",
  validateSearch: deliveryFilters.validate,
  component: gated("deliveries", DeliveriesPage),
});
// Dispatch (P8-08)
const dispatchRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/dispatch",
  validateSearch: boardFilters.validate,
  component: gated("dispatch", DispatchPage),
});
// Plan the week (P8-74)
const planRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/plan",
  component: gated("plan", PlanPage),
});
// Incidents (P8-09)
const incidentsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/incidents",
  validateSearch: incidentFilters.validate,
  component: gated("incidents", IncidentsPage),
});
// Customers (P8-10)
const customersRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/customers",
  validateSearch: customerFilters.validate,
  component: gated("customers", CustomersPage),
});
const customerRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/customers/$customerId",
  validateSearch: (search: Record<string, unknown>): { tab?: (typeof CUSTOMER_TABS)[number] } => ({
    tab: CUSTOMER_TABS.find((t) => t === search.tab),
  }),
  component: gated("customers", CustomerPage),
});
// Businesses (P8-11)
const businessesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/businesses",
  validateSearch: businessFilters.validate,
  component: gated("businesses", BusinessesPage),
});
const businessRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/businesses/$businessId",
  validateSearch: (search: Record<string, unknown>): { tab?: (typeof BUSINESS_TABS)[number] } => ({
    tab: BUSINESS_TABS.find((t) => t === search.tab),
  }),
  component: gated("businesses", BusinessPage),
});
// Fleet (P8-12): one page, the view and an open truck in the URL
const fleetRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/fleet",
  validateSearch: fleetFilters.validate,
  component: gated("fleet", FleetPage),
});
// The live fleet map (P8-51)
const fleetMapRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/fleet/map",
  validateSearch: fleetMapFilters.validate,
  component: gated("live-map", FleetMapPage),
});
// Partners (P8-13)
const partnersRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/partners",
  validateSearch: partnerFilters.validate,
  component: gated("partners", PartnersPage),
});
const partnerRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/partners/$partnerId",
  validateSearch: (search: Record<string, unknown>): { tab?: (typeof PARTNER_TABS)[number] } => ({
    tab: PARTNER_TABS.find((t) => t === search.tab),
  }),
  component: gated("partners", PartnerPage),
});
// Invoices (P8-14)
const invoicesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/invoices",
  validateSearch: invoiceFilters.validate,
  component: gated("invoices", InvoicesPage),
});
const newInvoiceRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/invoices/new",
  component: gated("invoices", NewInvoicePage),
});
const invoiceRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/invoices/$invoiceId",
  component: gated("invoices", InvoicePage),
});
const editInvoiceRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/invoices/$invoiceId/edit",
  component: gated("invoices", EditInvoicePage),
});
// Payments (P8-15)
const paymentsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/payments",
  validateSearch: paymentFilters.validate,
  component: gated("payments", PaymentsPage),
});
const paymentRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/payments/$paymentId",
  component: gated("payments", PaymentPage),
});
// Wallets (P8-16)
const walletsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/wallets",
  validateSearch: walletFilters.validate,
  component: gated("wallets", WalletsPage),
});
const walletRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/wallets/$walletId",
  component: gated("wallets", WalletPage),
});
// Payouts (P8-17)
const payoutsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/payouts",
  validateSearch: payoutFilters.validate,
  component: gated("payouts", PayoutsPage),
});
const payoutRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/payouts/$payoutId",
  component: gated("payouts", PayoutPage),
});
// Products (P8-18)
const productsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/products",
  component: gated("products", ProductsPage),
});
const productRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/products/$productId",
  component: gated("products", ProductPage),
});
// Pricing (P8-19)
const pricingRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/pricing",
  validateSearch: pricingFilters.validate,
  component: gated("pricing", PricingPage),
});
const ruleRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/pricing/$ruleId",
  component: gated("pricing", RulePage),
});
// Vouchers (P8-20)
const vouchersRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/vouchers",
  validateSearch: voucherFilters.validate,
  component: gated("vouchers", VouchersPage),
});
// Service zones (P8-21)
const zonesRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/zones",
  validateSearch: zoneFilters.validate,
  component: gated("zones", ZonesPage),
});
// Settings › Team (P8-22)
const staffRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/staff",
  validateSearch: staffFilters.validate,
  component: gated("staff", StaffPage),
});
const staffMemberRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/staff/$staffId",
  component: gated("staff", StaffMemberPage),
});
const auditRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/audit",
  validateSearch: auditFilters.validate,
  component: gated("audit", AuditPage),
});

// Settings › Connections (P8-23)
const integrationsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/integrations",
  validateSearch: integrationFilters.validate,
  component: gated("integrations", IntegrationsPage),
});
const integrationRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/integrations/$integrationId",
  validateSearch: integrationFilters.validate,
  component: gated("integrations", IntegrationPage),
});
const accountingRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/accounting",
  validateSearch: accountingFilters.validate,
  component: gated("accounting", AccountingPage),
});

// Tank sensors; Settings › Developers and Cut-over (P8-25)
const sensorsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/sensors",
  validateSearch: sensorFilters.validate,
  component: gated("sensors", SensorsPage),
});
const apiTokensRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/api-tokens",
  validateSearch: tokenFilters.validate,
  component: gated("api-tokens", ApiTokensPage),
});
const webhooksRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/webhooks",
  validateSearch: webhookFilters.validate,
  component: gated("webhooks", WebhooksPage),
});
const failedEventsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/failed-events",
  component: gated("failed-events", FailedEventsPage),
});
// Settings › Operations (P8-36)
const shiftChecklistRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/shift-checklist",
  component: gated("shift-checklist", ShiftChecklistPage),
});
// Settings › Operations (P8-49)
const proofChecklistRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/proof-checklist",
  component: gated("proof-checklist", ProofChecklistPage),
});
// Settings › Operations (P8-53)
// My profile and Settings › Company (P8-54). My profile has no gate: everyone has one.
const accountRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/account",
  component: AccountPage,
});
const brandingRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/branding",
  component: gated("branding", BrandingPage),
});
// Settings › Company › Invoice details (P8-70)
const invoiceDetailsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/invoice-details",
  component: gated("invoice-details", InvoiceDetailsPage),
});
// Alerts (P11-12)
const alertsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/alerts",
  component: gated("alerts", AlertsPage),
});
const dispatchAlertsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/dispatch-alerts",
  component: gated("dispatch-alerts", DispatchAlertsPage),
});
const deliveryEtaRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/delivery-eta",
  component: gated("delivery-eta", DeliveryEtaPage),
});
// Settings › Operations (P15-14)
const statusReasonsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/status-reasons",
  component: gated("status-reasons", StatusReasonsPage),
});
const readinessRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings/readiness",
  validateSearch: readinessFilters.validate,
  component: gated("readiness", ReadinessPage),
});

const builtRoutes = [
  ordersRoute,
  newOrderRoute,
  orderRoute,
  deliveryRoute,
  deliveriesRoute,
  dispatchRoute,
  planRoute,
  incidentsRoute,
  customersRoute,
  customerRoute,
  businessesRoute,
  businessRoute,
  fleetRoute,
  fleetMapRoute,
  partnersRoute,
  partnerRoute,
  invoicesRoute,
  newInvoiceRoute,
  invoiceRoute,
  editInvoiceRoute,
  paymentsRoute,
  paymentRoute,
  walletsRoute,
  walletRoute,
  payoutsRoute,
  payoutRoute,
  productsRoute,
  productRoute,
  pricingRoute,
  ruleRoute,
  vouchersRoute,
  zonesRoute,
  staffRoute,
  staffMemberRoute,
  auditRoute,
  integrationsRoute,
  integrationRoute,
  accountingRoute,
  sensorsRoute,
  apiTokensRoute,
  webhooksRoute,
  failedEventsRoute,
  readinessRoute,
  shiftChecklistRoute,
  proofChecklistRoute,
  dispatchAlertsRoute,
  deliveryEtaRoute,
  statusReasonsRoute,
  accountRoute,
  brandingRoute,
  invoiceDetailsRoute,
  alertsRoute,
];

/** /settings opens the first Settings page the role may see. */
const settingsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/settings",
  beforeLoad: async ({ context }) => {
    const session = await me(context);
    const first = SETTINGS_MENU.flatMap((s) => s.items).find((i) => allowed(i.gate, session?.permissions ?? []));
    throw redirect({ href: first?.path ?? "/" });
  },
});

/** Development builds only: the list, record and form kit with made-up data. */
const kitRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "/dev/kit",
  validateSearch: kitFilters.validate,
  component: KitPage,
});
const devRoutes = import.meta.env?.DEV ? [kitRoute] : [];

const routeTree = rootRoute.addChildren([
  signInRoute,
  setPasswordRoute,
  forgotPasswordRoute,
  chooseCompanyRoute,
  assistantAccessRoute,
  consoleRoute.addChildren([settingsRoute, ...builtRoutes, ...menuRoutes, ...devRoutes]),
]);

/**
 * Search params as plain query strings. Every console filter is a string, and the router's
 * default (JSON) would write a digits-only search as `?q=%224853%22`.
 */
export const parseSearch = (search: string): Record<string, string> =>
  Object.fromEntries(new URLSearchParams(search.startsWith("?") ? search.slice(1) : search));

export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}

export function createConsoleRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", parseSearch, stringifySearch });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createConsoleRouter>;
  }
}
